// track: anonymous counts, nothing personal and no cookies.
// - A page-load ping lists which slots were found on the page and which spots were missing; the server
//   counts pageviews and downtime from it. s.js sends it once the page has settled.
// - An impression counts once per slot per page view, after 1 continuous second with at least half of it
//   on screen (30% for a large slot, 242,500 px² or more): the IAB/MRC rule.
// - A click on a sponsor sends a beacon; the link itself goes straight to the sponsor.
// Beacons are plain text, so no CORS preflight, and they never hold the page up.

/**
 * @param {string} api  the embed API
 * @param {string} key  the site's embed key
 * @param {boolean} off  the owner's own browser: count nothing, only tell the server the script ran
 */
export function tracker(api, key, off) {
    /** @param {'p' | 'i' | 'c'} what  p: page-load ping (and our errors), i: impressions, c: a click @param {object} body @param {boolean} [always]  sent from the owner's browser too */
    const send = (what, body, always = false) => {
        if (off && !always) return;
        const url = `${api}/${what}`, text = JSON.stringify({ key, ...body });
        try {
            // the beacon queue can be full: then a keepalive fetch, which also outlives the page
            if (!navigator.sendBeacon(url, text)) fetch(url, { method: 'POST', body: text, keepalive: true, credentials: 'omit' }).catch(() => {});
        } catch { /* counting must never break the page */ }
    };
    /** @type {Set<string>} */
    const counted = new Set();
    /** @type {string[]} */
    let queue = [];
    let timer = 0;
    // The server counts an impression only after the visitor's pageview: until the first ping has
    // gone out, seen sponsors wait and ride along inside it (like a click before the ping).
    let pinged = false;
    const flush = () => {
        if (!pinged) return;
        if (queue.length) send('i', { slots: queue });
        queue = [];
    };
    /** @type {Map<Element, number>}  a sponsor on screen enough, waiting out its second */
    const timers = new Map();
    const seen = new IntersectionObserver(entries => {
        for (const e of entries) {
            const r = e.boundingClientRect, box = /** @type {HTMLElement} */ (e.target);
            if (e.intersectionRatio >= (r.width * r.height >= 242500 ? 0.3 : 0.5)) {
                if (!timers.has(box)) timers.set(box, setTimeout(() => {
                    const id = box.dataset.sl || '';
                    timers.delete(box);
                    seen.unobserve(box);
                    if (counted.has(id)) return;
                    counted.add(id);
                    queue.push(id);
                    clearTimeout(timer);
                    timer = setTimeout(flush, 1000);
                }, 1000));
            } else {
                clearTimeout(timers.get(box));
                timers.delete(box);
            }
        }
    }, { threshold: [0, 0.3, 0.5] });
    addEventListener('pagehide', flush);

    return {
        /** A new page view starts (first load or a route change): impressions count afresh. */
        reset() {
            flush();
            counted.clear();
            seen.disconnect();
            for (const t of timers.values()) clearTimeout(t);
            timers.clear();
        },
        /**
         * The page-load ping: a pageview, and which slots were found or missing on it.
         * @param {string} path @param {string[]} found @param {string[]} missing
         * @param {boolean} settled  sent after the page settled; misses only count when true
         * @param {string} [click]  a sponsor clicked before this ping went out: counted after the pageview
         * @param {number} [entry]  on a leaderboard: the entry clicked
         */
        page(path, found, missing, settled, click, entry) {
            // the owner's visit counts nothing, but keeps a quiet site open for booking (script seen)
            if (off) return send('p', { owner: true }, true);
            send('p', { path, found, missing, settled, click, entry, seen: queue.length ? queue : undefined });
            queue = [];
            pinged = true;
        },
        /** A visitor clicked this slot's sponsor (a leaderboard: its entry). @param {string} slot @param {number} [entry] */
        click(slot, entry) {
            send('c', { slot, entry });
        },
        /** Count this sponsor when it is seen. @param {HTMLElement} box */
        watch(box) {
            if (!counted.has(box.dataset.sl || '')) seen.observe(box);
        },
        /** Report our own error, so we hear about it before owners do. @param {unknown} err */
        error(err) {
            send('p', { error: String(err instanceof Error ? err.message : err).slice(0, 200) });
        },
    };
}
